<script setup lang="ts">
import { ref, watch } from 'vue';

const props = defineProps<{ modelValue: boolean; title?: string }>();
const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>();

const dragY = ref(0);
const dragStart = ref<number | null>(null);

function close() {
  emit('update:modelValue', false);
}

function onTouchStart(e: TouchEvent) {
  dragStart.value = e.touches[0].clientY;
  dragY.value = 0;
}

function onTouchMove(e: TouchEvent) {
  if (dragStart.value === null) return;
  const dy = e.touches[0].clientY - dragStart.value;
  dragY.value = Math.max(0, dy);
}

function onTouchEnd() {
  if (dragY.value > 100) close();
  dragY.value = 0;
  dragStart.value = null;
}

watch(
  () => props.modelValue,
  (open) => {
    document.body.style.overflow = open ? 'hidden' : '';
  },
);
</script>

<template>
  <Teleport to="body">
    <div v-if="modelValue" class="sheet-overlay" @click="close">
      <div
        class="sheet"
        :style="{ transform: `translateY(${dragY}px)` }"
        @click.stop
      >
        <div
          class="sheet__handle"
          @touchstart.passive="onTouchStart"
          @touchmove.passive="onTouchMove"
          @touchend="onTouchEnd"
        >
          <span class="sheet__pill" />
        </div>
        <h3 v-if="title" class="sheet__title">{{ title }}</h3>
        <div class="sheet__body">
          <slot />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.sheet-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 100;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}
.sheet {
  width: 100%;
  max-width: 480px;
  max-height: 85vh;
  overflow-y: auto;
  background: var(--bg-elevated);
  border-radius: var(--r-xl) var(--r-xl) 0 0;
  padding: 0 var(--sp-4) var(--sp-6);
  transition: transform 0.18s ease-out;
  border-top: 1px solid var(--hairline);
}
.sheet__handle {
  display: flex;
  justify-content: center;
  padding: 12px 0 8px;
  cursor: grab;
}
.sheet__pill {
  width: 36px;
  height: 4px;
  border-radius: var(--r-full);
  background: var(--text-tertiary);
}
.sheet__title {
  font-family: var(--font-display);
  font-size: var(--fs-xl);
  margin-bottom: var(--sp-3);
}
.sheet__body { padding-bottom: env(safe-area-inset-bottom); }
</style>
